import { updateCart } from '~/services/actions/CSU/update-cart';
import {
  getBillingAddressAttribute,
  getDefaultHeaderAttribute,
  getProgressiveAttributes,
} from '~/utils/checkout/extract-attribute-values';
import { onlinePayCheckout } from '~/services/actions/CSU/online-pay-checkout';
import { onlinePayCheckoutSettings } from '~/utils/payment/submit-order-settings';
import { TokenizedPaymentCard } from '~/interfaces/payment';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { wmapStore } from '~/store/wmap-store';
import { IFormInitialState } from '~/components/billing-address/errorLogic';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { submitPaymentUtag } from '~/utils/tealium';
import { setDocCookies } from '~/utils/doc-cookies';
import { extractCardType } from '../utils';
import { paymentErrorRedirect } from '../payment/payment-error-redirect';
import { TYPES } from '~/modules/payment/constants';
import { IGetPricing } from '~/interfaces/commerce-types';

interface IPayNow {
  tokenizedCard: TokenizedPaymentCard;
  isForterValidationEnabled: boolean;
  cartId: string;
  orderDetails: ISalesOrderResponse;
  router: AppRouterInstance;
  affirmPromoValue?: string;
  options?: {
    progressiveDetails?: IGetPricing;
    toggleSignModal?: () => void;
    isProgressive?: boolean;
    setOrderInProgress?: (inProgress: boolean) => void;
    progressiveAmountDue?: number;
  };
  isForterValidationEnabledForAdyen?: boolean;
}

export default async function payNow({
  tokenizedCard,
  isForterValidationEnabled,
  cartId,
  orderDetails,
  router,
  options,
  affirmPromoValue,
  isForterValidationEnabledForAdyen,
}: IPayNow) {
  const paymentOption = wmapStore.getState().selectedPaymentOption;
  const userSelectedAmount =
    options?.isProgressive && options.progressiveAmountDue
      ? options.progressiveAmountDue
      : wmapStore.getState().userSelectedAmount;
  const billingAddressState: IFormInitialState =
    wmapStore.getState().billingAddressState;
  const billingAddress = extractBillingAddress(billingAddressState);
  const cart = await updateCart(
    {
      Id: cartId,
      ReceiptEmail: billingAddress.Email,
      AttributeValues: [
        ...getBillingAddressAttribute(billingAddress),
        ...getDefaultHeaderAttribute(orderDetails),
        ...getProgressiveAttributes(options?.progressiveDetails),
      ],
    },
    cartId,
  );

  const payload = {
    cartTenderLines: await onlinePayCheckoutSettings({
      tokenizedCard,
      isForterValidationEnabled,
      billingAddress,
      userSelectedAmount,
      paymentOption,
      affirmPromoValue,
      isForterValidationEnabledForAdyen,
    }),
    cartId: cart.Id,
    receiptEmail: billingAddress.Email,
    orderBalanceDetails: JSON.stringify({
      '@odata.context':
        process.env.D365_CRT_URL +
        '/Commerce/$metadata#MFI.Extensions.CommerceRuntime.OnlinePay.Responses.MFIGetSaleOrderBalanceResponse',
      SaleOrderBalanceData: orderDetails,
    }),
    cartVersion: cart.Version,
  };
  const cardType = extractCardType(tokenizedCard?.CardTypeId);
  const synchronyOfferName =
    paymentOption?.toLowerCase() === TYPES.SYNCHRONY.toLowerCase()
      ? wmapStore.getState().syfDetails?.offerName || ''
      : '';
  submitPaymentUtag(
    cardType,
    orderDetails,
    userSelectedAmount,
    synchronyOfferName,
  );
  try {
    const salesOrder = await onlinePayCheckout(payload);
    const cityStateZipFromPaymentPage = `${billingAddress.City}, ${billingAddress.State} ${billingAddress.ZipCode}`;
    const fullName = `${billingAddress.FirstName} ${billingAddress.LastName}`;

    const cookieData = {
      userSelectedAmount: userSelectedAmount,
      email: billingAddress.Email,
      cardLastFourDigit:
        tokenizedCard.CardTokenInfo?.MaskedCardNumber?.slice(-4),
      cardType: cardType,
      fullName: fullName,
      cityStateZip: cityStateZipFromPaymentPage,
      address: billingAddress.Street,
      apartment: billingAddress.StreetNumber,
      quotationOrSalesId: orderDetails.QuotationId || orderDetails.SalesId,
      syfDetails: JSON.stringify(wmapStore.getState().syfDetails),
      selectedPaymentOption: paymentOption,
      progressiveDetails: options?.progressiveDetails
        ? JSON.stringify(options.progressiveDetails)
        : undefined,
    };

    if (salesOrder?.Status) {
      setDocCookies(cookieData, { secure: true, sameSite: 'Strict' });
      window.location.replace('makeapayment/confirmation');
    } else {
      const errorCode = salesOrder?.StatusCode || '500';
      if (options?.isProgressive && options.progressiveDetails) {
        options?.setOrderInProgress?.(false);
        options.toggleSignModal?.();
      }
      paymentErrorRedirect(
        errorCode,
        orderDetails.QuotationId || orderDetails.SalesId,
        router,
      );
    }
  } catch (e) {
    console.error(e);
    throw new Error('Fail to placeOrder: fail to checkout');
  }
}

function extractBillingAddress(billingAddressState: IFormInitialState) {
  return {
    ThreeLetterISORegionName: 'USA',
    AddressTypeValue: 6,
    FirstName: billingAddressState.firstName.value,
    LastName: billingAddressState.lastName.value,
    Phone: billingAddressState.phoneNumber.value,
    Email: billingAddressState.emailAddress.value,
    Street: billingAddressState.address.value,
    StreetNumber: billingAddressState.apartment.value,
    ZipCode: billingAddressState.zipcode.value,
    City: billingAddressState.city.value,
    State: billingAddressState.state.value,
    TwoLetterISORegionName: 'US',
    Name: `${billingAddressState.firstName.value} ${billingAddressState.lastName.value}`
      .trim()
      .replace(/\s+/g, ' '),
  };
}
